import { isParkedStepStatus, WorkflowRunStepStatus } from './types';

/**
 * Run-level rollup statuses, as produced by the server's `syncRunStatus`.
 *
 * Shares its spelling with {@link WorkflowRunStepStatus} because a run's status
 * is derived from its steps', but the two are not interchangeable: a run never
 * sits at `running` — a step being in flight vs parked is a step-level
 * distinction that rolls up to the single `incomplete` bucket.
 */
export enum WorkflowRunRollupStatus {
  Queued = 'queued',
  Incomplete = 'incomplete',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled',
  Stopped = 'stopped',
}

export type TerminalStepStatus =
  | WorkflowRunStepStatus.Completed
  | WorkflowRunStepStatus.Failed
  | WorkflowRunStepStatus.Cancelled
  | WorkflowRunStepStatus.Stopped
  // A bucket absorbing the run halts the branch and retains the data; the step
  // is final, so it must not be reopened or re-claimed. It rolls up to a
  // `completed` run status (see `syncRunStatus`), so there is no matching
  // terminal *run* status.
  | WorkflowRunStepStatus.Absorbed;

export type TerminalRunStatus =
  | WorkflowRunRollupStatus.Completed
  | WorkflowRunRollupStatus.Failed
  | WorkflowRunRollupStatus.Cancelled
  | WorkflowRunRollupStatus.Stopped;

/** Statuses after which a step instance is final and must not be reopened. */
export const TERMINAL_STEP_STATUSES: readonly TerminalStepStatus[] = [
  WorkflowRunStepStatus.Completed,
  WorkflowRunStepStatus.Failed,
  WorkflowRunStepStatus.Cancelled,
  WorkflowRunStepStatus.Stopped,
  WorkflowRunStepStatus.Absorbed,
];

/** Statuses after which a run is final. Same spellings as the step set. */
export const TERMINAL_RUN_STATUSES: readonly TerminalRunStatus[] = [
  WorkflowRunRollupStatus.Completed,
  WorkflowRunRollupStatus.Failed,
  WorkflowRunRollupStatus.Cancelled,
  WorkflowRunRollupStatus.Stopped,
];

const TERMINAL_STEP_STATUS_SET: ReadonlySet<string> = new Set(
  TERMINAL_STEP_STATUSES
);

const TERMINAL_RUN_STATUS_SET: ReadonlySet<string> = new Set(
  TERMINAL_RUN_STATUSES
);

export function isTerminalStepStatus(
  status: string | null | undefined
): status is TerminalStepStatus {
  return status !== null && status !== undefined
    ? TERMINAL_STEP_STATUS_SET.has(status)
    : false;
}

/**
 * Statuses that mean a run ended in failure.
 *
 * A positive list, deliberately: a new terminal status that is *not* a failure
 * (a future "skipped" or "no change") must never be mistaken for one and
 * re-run. Extend this set when a genuine new failure status is introduced.
 */
export const FAILED_RUN_STATUSES: readonly WorkflowRunRollupStatus[] = [
  WorkflowRunRollupStatus.Failed,
];

const FAILED_RUN_STATUS_SET: ReadonlySet<string> = new Set(FAILED_RUN_STATUSES);

/** Whether a run's status is one of {@link FAILED_RUN_STATUSES}. */
export function isFailedRunStatus(status: string | null | undefined): boolean {
  return status !== null && status !== undefined
    ? FAILED_RUN_STATUS_SET.has(status)
    : false;
}

export function isTerminalRunStatus(
  status: string | null | undefined
): status is TerminalRunStatus {
  return status !== null && status !== undefined
    ? TERMINAL_RUN_STATUS_SET.has(status)
    : false;
}

/**
 * Renders the terminal step statuses as a SQL `IN (...)` list, so the server's
 * SQL fragments cannot drift from this enum the way the five hand-maintained
 * copies of this set did.
 */
export function terminalStepStatusesSqlList(): string {
  return `(${TERMINAL_STEP_STATUSES.map((status) => `'${status}'`).join(
    ', '
  )})`;
}

export type StepStatusTransition = 'apply' | 'reject';

/**
 * Whether a step instance may move from `from` to `to`.
 *
 * Deliberately not a full status × status matrix. It encodes the two rules the
 * system actually relies on, and nothing speculative:
 *
 * 1. **Terminal → non-terminal is rejected.** Several writers race on a single
 *    instance — the node runner, the ATC run-lifecycle bridge, and whatever
 *    durably completes an interrupted tool. A bridge event landing after a
 *    wake-up already completed the step would otherwise roll `completed` back to
 *    `incomplete` and replace the recorded output with the incomplete envelope.
 *
 * 2. **`stopped` → anything is rejected.** `stopped` is written in exactly one
 *    place (the filter node runner) and means a gate evaluated false and
 *    intentionally halted this branch. This narrows the older "terminal →
 *    terminal is always allowed" rule, which let a late saga error overwrite an
 *    intentional halt with `failed` — and since the run rollup ranks `failed`
 *    first, turned a working filter into a failed run. Cancelling or re-failing
 *    the other terminal statuses still works.
 *
 * Everything else applies. Pairs like `queued → incomplete` or `running →
 * queued` are nonsense, but nothing attempts them, so rejecting them here would
 * buy assertions rather than bug fixes. Add a rule when a real caller needs one.
 */
export function stepStatusTransition(
  from: string | null | undefined,
  to: WorkflowRunStepStatus
): StepStatusTransition {
  if (from === null || from === undefined) {
    return 'apply';
  }
  if (from === WorkflowRunStepStatus.Stopped) {
    return 'reject';
  }
  if (isTerminalStepStatus(from) && !isTerminalStepStatus(to)) {
    return 'reject';
  }
  return 'apply';
}

export { isParkedStepStatus };
